/**
 * WatchButton — reusable toggle for watching/unwatching a resource.
 * Renders an eye icon that toggles between outline (not watching) and
 * filled (watching). Fetches status on mount, toggles optimistically.
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as watchApi from '../api/watch-api';
import type { WatchResourceType } from '../api/watch-api';

interface WatchButtonProps {
  readonly resourceType: WatchResourceType;
  readonly resourceId: string;
  /** boardId for cards, libraryId for pages — needed for API paths. */
  readonly parentId?: string;
  /** Optional compact mode for tight spaces (card headers). */
  readonly compact?: boolean;
}

export function WatchButton({ resourceType, resourceId, parentId, compact }: WatchButtonProps): React.ReactElement {
  const [watching, setWatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Fetch initial watch status
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const status = await watchApi.getWatchStatus(resourceType, resourceId, parentId);
        if (!cancelled) setWatching(status.watching);
      } catch {
        // Default to not watching if status check fails
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [resourceType, resourceId, parentId]);

  const handleToggle = useCallback(async () => {
    if (toggling) return;
    setToggling(true);

    // Optimistic update
    const previousState = watching;
    setWatching(!watching);

    try {
      if (previousState) {
        await watchApi.unwatch(resourceType, resourceId, parentId);
      } else {
        await watchApi.watch(resourceType, resourceId, parentId);
      }
    } catch {
      // Revert on failure
      setWatching(previousState);
    } finally {
      setToggling(false);
    }
  }, [watching, toggling, resourceType, resourceId, parentId]);

  if (loading) return <></>;

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={toggling}
      className={`inline-flex items-center gap-1 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        compact
          ? 'p-1 text-xs'
          : 'px-2.5 py-1.5 text-xs border'
      } ${
        watching
          ? 'text-primary bg-primary/10 border-primary/30 hover:bg-primary/20'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent border-input'
      }`}
      aria-pressed={watching}
      aria-label={watching ? 'Stop watching' : 'Watch for changes'}
      title={watching ? 'Watching — click to unwatch' : 'Watch for changes'}
    >
      {/* Eye icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}
        aria-hidden="true"
      >
        {watching ? (
          // Filled eye (watching)
          <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
        ) : (
          // Outline eye (not watching)
          <path fillRule="evenodd" d="M10 3C5.5 3 1.73 5.94 0 10c1.73 4.06 5.5 7 10 7s8.27-2.94 10-7c-1.73-4.06-5.5-7-10-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" clipRule="evenodd" />
        )}
        {watching && (
          <path d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
        )}
      </svg>
      {!compact && (
        <span>{watching ? 'Watching' : 'Watch'}</span>
      )}
    </button>
  );
}
